import { useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { PublicationCovers } from '@modules-rocco/subscription/config/publication-covers';
import type { DigitalPublicationOption } from '@modules-rocco/subscription/api/get-digital-publication-options.server';
import { SubscribePublicationsTrigger } from '@modules-rocco/subscription/component/SubscribePublicationsTrigger';
import { SubscribePublicationsModal } from '@modules-rocco/subscription/component/SubscribePublicationsModal';
import { detailCtaIcon } from '~/ui-rocco/pages/detail-shared';
import { DIRECTORY_GUTTER_X } from './EditorialArticleGrid';

interface DigitalPublicationsRowProps {
    /**
     * Optional, and defaulted below. The catalogue answers with an empty list
     * on failure rather than throwing, and a row that assumed the prop was
     * always an array took the whole page down with it whenever the loader
     * data and the component were briefly out of step.
     */
    publications?: DigitalPublicationOption[];
    /**
     * The covers alone, without the row's own line and Subscribe. For the
     * spot under the directory's opening, which already carries both.
     */
    hideIntro?: boolean;
    className?: string;
}

/**
 * How many covers the row shows: the four series, across the measure. The
 * catalogue can hold more, and a fifth cut off at the edge is what the row
 * used to run past — now the four fill the width and the rest are left off.
 */
const DISPLAYED = 4;

/** The line the section opens with, as the design sets it (Figma 12:630). */
const INTRO_COPY =
    "New Zealand's most exciting architectural projects, products and professionals featured weekly in an interactive experience";

/**
 * The publications, under the directory's opening and above the category
 * tabs (Figma 0:81).
 *
 * Four covers across the listing's measure, each opening what that
 * publication is and the chance to subscribe to it. On a phone the four are
 * a scroller a reader drags instead.
 *
 * Sizes in rem at the 1920 artboard (÷16), base + md: only, matching the
 * articles-revamp siblings.
 */
export const DigitalPublicationsRow = ({
    publications = [],
    hideIntro = false,
    className,
}: DigitalPublicationsRowProps) => {
    const [openPublication, setOpenPublication] =
        useState<DigitalPublicationOption | null>(null);
    const shown = publications.slice(0, DISPLAYED);
    if (shown.length === 0) return null;

    return (
        <section
            aria-label="Digital publications"
            className={cn('w-full', className)}
        >
            {hideIntro ? null : (
                <>
                    {/* On the grid's margin, not the frame's 50px: the section sits
                directly above the listing, and two different page margins
                stacked read as a misalignment rather than as two sections. */}
                    <div
                        className={cn(
                            'flex items-start justify-between gap-8 max-md:flex-col',
                            DIRECTORY_GUTTER_X,
                        )}
                    >
                        <p className="m-0 font-gestura text-[1.5rem] leading-[1.25] font-normal tracking-[-0.025em] text-[#181818] md:max-w-[60.375rem] md:text-[2.0625rem]">
                            {INTRO_COPY}
                        </p>
                        {/* The section's own Subscribe, moved off the tab bar: it
                    belongs with the publications it subscribes to, not with
                    the category filters. Geometry comes from the button
                    system's xl step, which is the design's 60px. */}
                        <SubscribePublicationsTrigger
                            label="Subscribe"
                            size="xl"
                            className="shrink-0"
                        />
                    </div>
                </>
            )}

            {/* Four equal columns on the listing's own margins, so the first
                cover starts on the page edge and the last ends on it. On a
                phone the gutter is padding on the scroller itself, so the
                first cover starts on the margin and the last can still
                travel to the edge. */}
            <div
                className={cn(
                    'max-md:flex max-md:gap-[1.5rem] max-md:overflow-x-auto md:grid md:grid-cols-4 md:gap-[1.5rem]',
                    !hideIntro && 'mt-[4.0625rem]',
                    DIRECTORY_GUTTER_X,
                    // No visible scrollbar under the covers on a phone; the
                    // cut-off cover is the affordance.
                    '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
                )}
            >
                {shown.map((publication) => (
                    <PublicationCard
                        key={publication.hubspotID}
                        publication={publication}
                        onOpen={() => setOpenPublication(publication)}
                    />
                ))}
            </div>

            {/* The site's own subscribe gate, arriving with this
                publication ticked, rather than a second modal describing the
                same four things in different words. */}
            <SubscribePublicationsModal
                open={openPublication !== null}
                initialNewsletterType={openPublication?.newsletterType}
                onClose={() => setOpenPublication(null)}
            />
        </section>
    );
};

/**
 * The cover the row draws.
 *
 * `PublicationCovers` is the licensed series artwork and wins for real
 * publications, which is the whole reason it exists — the artwork stops moving
 * under the design as issues come and go. A placeholder carries the frame's own
 * cover instead and says so, so the stand-in row shows the design's art rather
 * than falling through to the same four covers for five entries.
 */
const coverFor = (publication: DigitalPublicationOption) =>
    publication.isPlaceholder
        ? publication.coverImage
        : (PublicationCovers[publication.newsletterType] ??
          publication.coverImage);

/**
 * One publication: its cover, what it is called, the invitation under it,
 * and the control that opens it.
 *
 * The whole cover is the control as well as the plus, so a reader who aims at
 * the artwork rather than the small circle still gets there.
 */
const PublicationCard = ({
    publication,
    onOpen,
}: {
    publication: DigitalPublicationOption;
    onOpen: () => void;
}) => {
    const cover = coverFor(publication);

    return (
        <div className="flex flex-col max-md:w-[14rem] max-md:shrink-0 md:w-full">
            {/* The frame's 446 x 619 cover, at whatever width a quarter of
                the measure gives it. A plain <img>, not rocco's `Image`: these are
                bundled assets on a build-hashed path, which the CDN loader has
                no origin to resolve. */}
            <button
                type="button"
                onClick={onOpen}
                aria-label={`About ${publication.title}`}
                className="relative aspect-[446/619] w-full cursor-pointer overflow-hidden rounded-[0.5rem] border-0 bg-[#f1efec] p-0"
            >
                {cover ? (
                    <img
                        src={cover}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 size-full object-cover object-top"
                    />
                ) : null}
            </button>

            {/* The plus ranges right to the cover's edge, so the row of
                them lines up with the row of covers above. */}
            <div className="mt-[0.8125rem] flex items-start justify-between gap-[0.625rem]">
                <div className="min-w-0 flex-1">
                    <p className="m-0 font-sans text-4 leading-[1.3] font-normal text-[#181818] md:text-[1.375rem]">
                        {publication.title}
                    </p>
                    {/* The invitation where the issue date was: a reader
                        looking at a cover wants to know what to do with it
                        more than when it last went out. A control like the
                        plus, so the line opens the same gate. At the phone's
                        caption size below md, as the title above it. */}
                    <button
                        type="button"
                        onClick={onOpen}
                        className="m-0 block cursor-pointer border-0 bg-transparent p-0 text-left font-sans text-3 leading-[1.125rem] font-normal text-[#707070] transition-colors hover:text-[#181818] md:text-[1.25rem] md:leading-[1.875rem]"
                    >
                        Click to Subscribe
                    </button>
                </div>
                {/* The product page's lifted white square, the one its Save
                    sits in under the logo, so the row's controls are the
                    detail pages' controls. */}
                <button
                    type="button"
                    onClick={onOpen}
                    aria-label={`About ${publication.title}`}
                    className={detailCtaIcon('white-lifted', 'shrink-0')}
                >
                    <span
                        aria-hidden="true"
                        className="relative block size-[1.125rem] md:size-[1.475rem]"
                    >
                        <span className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-[#181818]" />
                        <span className="absolute top-0 left-1/2 h-full w-px -translate-x-1/2 bg-[#181818]" />
                    </span>
                </button>
            </div>
        </div>
    );
};
